import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Stack, Chip } from '@mui/material';
import maplibregl, {
  type LngLatBoundsLike,
  type Map as MapLibreMap,
  type MapLayerMouseEvent,
} from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import MonitoringMapPointCard from './MonitoringMapPointCard';
import type { MonitoringMapFocus, MonitoringMapPoint } from '../../types/monitoring';
import {
  findMapPointById,
  GA4_VISITOR_COLOR,
  hasValidTargetCoordinates,
  kindColor,
} from '../../utils/monitoringPresentation';

const FRANCE_CENTER: [number, number] = [2.5, 46.6];
const FRANCE_BOUNDS: LngLatBoundsLike = [
  [-5.25, 41.25],
  [9.85, 51.15],
];
const DEFAULT_ZOOM = 5.5;
const SOURCE_ID = 'monitoring-points';
const MATCH_LINES_SOURCE_ID = 'monitoring-match-lines';
const MATCH_LINES_LAYER_ID = 'monitoring-match-lines-layer';
const LAYER_ID = 'monitoring-points-circles';

const MATCHING_COLOR = '#0ea5e9';
const CARD_OFFSET_X = 16;
const CARD_OFFSET_Y = 18;

type Props = {
  points: MonitoringMapPoint[];
  lostColor: string;
  foundColor: string;
  height?: number | string;
  highlightedPointId?: string | null;
  mapFocus?: MonitoringMapFocus | null;
  mapFocusToken?: number;
  layoutRevision?: number;
  onPointHover?: (point: MonitoringMapPoint | null) => void;
};

function zoomMapToFocus(map: MapLibreMap, focus: MonitoringMapFocus): void {
  if (focus.bounds) {
    map.fitBounds(focus.bounds, { padding: 56, maxZoom: 10, duration: 650 });
    return;
  }

  const targetLat = focus.targetLatitude;
  const targetLng = focus.targetLongitude;
  const hasDistinctTarget =
    targetLat != null &&
    targetLng != null &&
    Number.isFinite(targetLat) &&
    Number.isFinite(targetLng) &&
    (Math.abs(targetLat - focus.latitude) > 0.0001 || Math.abs(targetLng - focus.longitude) > 0.0001);

  if (hasDistinctTarget) {
    const bounds: LngLatBoundsLike = [
      [Math.min(focus.longitude, targetLng), Math.min(focus.latitude, targetLat)],
      [Math.max(focus.longitude, targetLng), Math.max(focus.latitude, targetLat)],
    ];
    map.fitBounds(bounds, { padding: 72, maxZoom: 11, duration: 650 });
    return;
  }

  map.easeTo({
    center: [focus.longitude, focus.latitude],
    zoom: 11,
    duration: 650,
  });
}

const FranceMonitoringMap: React.FC<Props> = ({
  points,
  lostColor,
  foundColor,
  height = '100%',
  highlightedPointId = null,
  mapFocus = null,
  mapFocusToken = 0,
  layoutRevision = 0,
  onPointHover,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const pointsByIdRef = useRef<Map<string, MonitoringMapPoint>>(new Map());
  const highlightedPointIdRef = useRef<string | null>(highlightedPointId ?? null);
  const hoveredPointRef = useRef<MonitoringMapPoint | null>(null);
  const [hoveredPoint, setHoveredPoint] = useState<MonitoringMapPoint | null>(null);
  const [cardPosition, setCardPosition] = useState<{ x: number; y: number } | null>(null);

  highlightedPointIdRef.current = highlightedPointId ?? null;

  const pointsById = useMemo(() => new Map(points.map((point) => [point.id, point])), [points]);
  pointsByIdRef.current = pointsById;

  const grouped = useMemo(() => {
    const counts = { lost: 0, found: 0, matching: 0, ga4: 0 };
    for (const point of points) {
      counts[point.kind] += 1;
    }
    return counts;
  }, [points]);

  const geoJson = useMemo(
    () => ({
      type: 'FeatureCollection' as const,
      features: points.map((point) => ({
        type: 'Feature' as const,
        geometry: {
          type: 'Point' as const,
          coordinates: [point.longitude, point.latitude] as [number, number],
        },
        properties: {
          id: point.id,
          kind: point.kind,
          highlighted: point.id === highlightedPointId,
          color: kindColor(point.kind, lostColor, foundColor, MATCHING_COLOR),
          radius: point.kind === 'ga4' ? Math.min(16, 7 + (point.active_users ?? 1) * 1.5) : null,
        },
      })),
    }),
    [points, lostColor, foundColor, highlightedPointId]
  );

  const matchLinesGeoJson = useMemo(
    () => ({
      type: 'FeatureCollection' as const,
      features: points
        .filter((point) => point.kind === 'matching' && hasValidTargetCoordinates(point))
        .map((point) => ({
          type: 'Feature' as const,
          geometry: {
            type: 'LineString' as const,
            coordinates: [
              [point.longitude, point.latitude],
              [point.target_longitude as number, point.target_latitude as number],
            ] as [number, number][],
          },
          properties: {
            id: point.id,
            highlighted: point.id === highlightedPointId,
          },
        })),
    }),
    [points, highlightedPointId]
  );

  const updateCardPosition = useCallback((point: MonitoringMapPoint | null) => {
    const map = mapRef.current;
    const container = containerRef.current;
    if (!map || !container || !point) {
      setCardPosition(null);
      return;
    }

    const projected = map.project([point.longitude, point.latitude]);
    const width = container.clientWidth;
    const heightPx = container.clientHeight;
    const cardWidth = 300;
    const cardHeight = 260;

    let x = projected.x + CARD_OFFSET_X;
    let y = projected.y - CARD_OFFSET_Y;

    if (x + cardWidth > width - 8) {
      x = projected.x - cardWidth - CARD_OFFSET_X;
    }
    if (y - cardHeight < 8) {
      y = projected.y + CARD_OFFSET_Y + 12;
    }
    if (x < 8) x = 8;
    if (y < 8) y = 8;
    if (y > heightPx - 8) y = heightPx - 8;

    setCardPosition({ x, y });
  }, []);

  const showPointCard = useCallback(
    (point: MonitoringMapPoint | null) => {
      hoveredPointRef.current = point;
      setHoveredPoint(point);
      onPointHover?.(point);
      updateCardPosition(point);
    },
    [onPointHover, updateCardPosition]
  );

  useEffect(() => {
    if (!highlightedPointId) return;
    const point = findMapPointById(points, highlightedPointId);
    if (point) {
      showPointCard(point);
    }
  }, [highlightedPointId, points, showPointCard]);

  useEffect(() => {
    if (!mapFocus || mapFocusToken <= 0) return;
    const map = mapRef.current;
    if (!map) return;

    const runZoom = () => zoomMapToFocus(map, mapFocus);
    if (map.isStyleLoaded()) {
      runZoom();
    } else {
      map.once('load', runZoom);
    }
  }, [mapFocus, mapFocusToken]);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: {
        version: 8,
        sources: {
          rasterTiles: {
            type: 'raster',
            tiles: [
              'https://a.tile.openstreetmap.org/{z}/{x}/{y}.png',
              'https://b.tile.openstreetmap.org/{z}/{x}/{y}.png',
              'https://c.tile.openstreetmap.org/{z}/{x}/{y}.png',
            ],
            tileSize: 256,
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
          },
        },
        layers: [{ id: 'raster-layer', type: 'raster', source: 'rasterTiles' }],
      },
      center: FRANCE_CENTER,
      zoom: DEFAULT_ZOOM,
      dragRotate: false,
      touchZoomRotate: false,
      pitchWithRotate: false,
    });

    map.addControl(
      new maplibregl.NavigationControl({ showCompass: false, visualizePitch: false }),
      'top-right'
    );

    const syncCardOnMove = () => {
      const current = hoveredPointRef.current;
      if (current) {
        updateCardPosition(current);
      }
    };

    map.on('load', () => {
      map.addSource(MATCH_LINES_SOURCE_ID, { type: 'geojson', data: matchLinesGeoJson });
      map.addLayer({
        id: MATCH_LINES_LAYER_ID,
        type: 'line',
        source: MATCH_LINES_SOURCE_ID,
        paint: {
          'line-color': [
            'case',
            ['boolean', ['get', 'highlighted'], false],
            MATCHING_COLOR,
            '#60a5fa',
          ],
          'line-width': [
            'case',
            ['boolean', ['get', 'highlighted'], false],
            3,
            1.75,
          ],
          'line-opacity': 0.85,
        },
      });

      map.addSource(SOURCE_ID, { type: 'geojson', data: geoJson });
      map.addLayer({
        id: LAYER_ID,
        type: 'circle',
        source: SOURCE_ID,
        paint: {
          'circle-radius': [
            'case',
            ['boolean', ['get', 'highlighted'], false],
            11,
            ['case', ['==', ['get', 'kind'], 'ga4'], ['coalesce', ['get', 'radius'], 9], ['case', ['==', ['get', 'kind'], 'matching'], 7, 8]],
          ],
          'circle-color': ['get', 'color'],
          'circle-opacity': 0.92,
          'circle-stroke-width': [
            'case',
            ['boolean', ['get', 'highlighted'], false],
            3,
            2,
          ],
          'circle-stroke-color': '#ffffff',
        },
      });
      map.fitBounds(FRANCE_BOUNDS, { padding: 32, duration: 0 });
    });

    map.on('mousemove', LAYER_ID, (event: MapLayerMouseEvent) => {
      map.getCanvas().style.cursor = 'pointer';
      const feature = event.features?.[0];
      if (!feature) return;
      const point = pointsByIdRef.current.get(String(feature.properties?.id ?? ''));
      if (point) {
        showPointCard(point);
      }
    });

    map.on('mouseleave', LAYER_ID, () => {
      map.getCanvas().style.cursor = '';
      if (!highlightedPointIdRef.current) {
        showPointCard(null);
      }
    });

    map.on('move', syncCardOnMove);
    map.on('zoom', syncCardOnMove);

    mapRef.current = map;

    return () => {
      map.off('move', syncCardOnMove);
      map.off('zoom', syncCardOnMove);
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const updateMap = () => {
      const linesSource = map.getSource(MATCH_LINES_SOURCE_ID) as maplibregl.GeoJSONSource | undefined;
      linesSource?.setData(matchLinesGeoJson);
      const source = map.getSource(SOURCE_ID) as maplibregl.GeoJSONSource | undefined;
      source?.setData(geoJson);
    };

    if (map.isStyleLoaded()) {
      updateMap();
    } else {
      map.once('load', updateMap);
    }
  }, [geoJson, matchLinesGeoJson]);

  useEffect(() => {
    if (hoveredPoint) {
      updateCardPosition(hoveredPoint);
    }
  }, [hoveredPoint, updateCardPosition]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const resize = () => {
      map.resize();
      const current = hoveredPointRef.current;
      if (current) {
        updateCardPosition(current);
      }
    };

    const timer = window.setTimeout(resize, 80);
    return () => window.clearTimeout(timer);
  }, [layoutRevision, updateCardPosition]);

  return (
    <Box
      sx={{
        position: 'relative',
        height,
        minHeight: height === '100%' ? 0 : 420,
        borderRadius: 2,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box ref={containerRef} sx={{ width: '100%', height: '100%' }} />

      {hoveredPoint && cardPosition ? (
        <Box
          sx={{
            position: 'absolute',
            left: cardPosition.x,
            top: cardPosition.y,
            transform: 'translateY(-100%)',
            zIndex: 5,
            maxWidth: 'calc(100% - 16px)',
          }}
        >
          <MonitoringMapPointCard point={hoveredPoint} lostColor={lostColor} foundColor={foundColor} />
        </Box>
      ) : null}

      <Stack
        direction="row"
        spacing={1}
        flexWrap="wrap"
        useFlexGap
        sx={{
          position: 'absolute',
          left: 12,
          bottom: 12,
          zIndex: 2,
          bgcolor: 'rgba(255,255,255,0.92)',
          borderRadius: 2,
          px: 1.25,
          py: 0.75,
          boxShadow: 2,
          pointerEvents: 'none',
        }}
      >
        <Chip
          size="small"
          label={`Perdu (${grouped.lost})`}
          sx={{ bgcolor: lostColor, color: '#1e293b', fontWeight: 700 }}
        />
        <Chip
          size="small"
          label={`Trouvé (${grouped.found})`}
          sx={{ bgcolor: foundColor, color: '#fff', fontWeight: 700 }}
        />
        <Chip
          size="small"
          label={`Matching (${grouped.matching})`}
          sx={{ bgcolor: MATCHING_COLOR, color: '#fff', fontWeight: 700 }}
        />
        {grouped.ga4 > 0 ? (
          <Chip
            size="small"
            label={`GA4 (${grouped.ga4})`}
            sx={{ bgcolor: GA4_VISITOR_COLOR, color: '#fff', fontWeight: 700 }}
          />
        ) : null}
      </Stack>
    </Box>
  );
};

export default FranceMonitoringMap;
